import { useCallback, useState } from 'react';
import { Button } from 'tuui';
import { Radio } from 'taboola-ultimate-ui';
import { RadioGroupField } from 'modules/campaigns/components/Form';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { STEP_IDS } from '../../config/stepIds';
import { NewEventHeader } from '../Headers/NewEventHeader';
import styles from './buttonConversionLocationSelector.module.scss';

const RADIO_OPTIONS = {
    SPECIFIC_PAGE: 'SPECIFIC_PAGE',
    CROSS_SITE: 'CROSS_SITE',
};

export const locationOptions = [
    {
        value: RADIO_OPTIONS.SPECIFIC_PAGE,
        title: (
            <FormattedMessage id="codeless.conversion.type.button.location.specific" defaultMessage="Specific Page" />
        ),
        description: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.specific.description"
                defaultMessage="Select the buttons that you wish to track exclusively on this specific page."
            />
        ),
    },
    {
        value: RADIO_OPTIONS.CROSS_SITE,
        title: (
            <FormattedMessage id="codeless.conversion.type.button.location.cross_site" defaultMessage="Cross-Site" />
        ),
        description: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.cross_site.description"
                defaultMessage="Select the buttons throughout the entire website that you wish to track."
            />
        ),
    },
];

export const ButtonConversionLocationSelector = ({ navigateToStepId, handleCancelWizard }) => {
    const [conversionLocationSelection, setConversionLocationSelection] = useState(RADIO_OPTIONS.SPECIFIC_PAGE);
    const onRadioChange = useCallback(value => setConversionLocationSelection(value), [setConversionLocationSelection]);

    const navigateToPreviousStep = () => navigateToStepId(STEP_IDS.CONVERSION_TYPE_SETUP);

    const navigateToNextStep = () => {
        const nextStepId =
            conversionLocationSelection === RADIO_OPTIONS.SPECIFIC_PAGE
                ? STEP_IDS.BUTTON_ANALYZER_STEP
                : STEP_IDS.GLOBAL_BUTTON_SETUP;
        navigateToStepId(nextStepId);
    };

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={navigateToPreviousStep} />
            <RadioGroupField
                groupTitle={
                    <FormattedMessage
                        id="codeless.conversion.location.header"
                        defaultMessage="Select a conversion location"
                    />
                }
                radioGroupName="conversionLocationSelection"
                onChange={onRadioChange}
                selectedValue={conversionLocationSelection}
                data-metrics-component="button-conversion-location-selector"
                className={styles['group']}
            >
                {locationOptions.map(({ value: optionValue, title, description }) => (
                    <Radio
                        labelClassName={styles['radio']}
                        descriptionClassName={styles['description']}
                        aria-label={`${optionValue}`}
                        key={`button-conversion-location-selector-${optionValue}`}
                        value={optionValue}
                        title={title}
                        description={description}
                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                        data-metrics-value={optionValue}
                        inputClassName={styles['input']}
                    />
                ))}
            </RadioGroupField>
            <footer className={styles['footer']}>
                <Button
                    onClick={handleCancelWizard}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={navigateToNextStep}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </div>
    );
};
